/**
 * Country / locale switcher shown in the header (web plan W2.1).
 *
 * Reads the LOCALES array, highlights the current locale, and, when the user
 * picks a different country, goes to the same kind of page there (web plan
 * MW7, @/seo/countrySwitch): the same brand, shop, store or promotion when it
 * shows in that country, else that country's home; the same list, search or
 * category; an account page stays where it is. Always rendered (even when only
 * one locale exists) so the header layout stays stable when more markets are
 * added.
 */
import { useContext, useEffect, useId, useRef, useState } from 'react';
import { getApolloContext } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { RESOLVE_ENTITY_PATH, SALE_PAGE } from '@/graphql/queries';
import { resolveVariables } from '@/graphql/entityRoutes';
import { ccToFlag } from '@/lib/countryFlag';
import { rememberLocale } from '@/lib/rememberedLocale';
import { LOCALES, localeKey, type Locale } from '@/seo/locales';
import { planCountrySwitch, resolveSwitchTarget, type EntityResolution } from '@/seo/countrySwitch';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { useSaleCountries } from '@/hooks/useSaleCountries';

export function CountrySelector() {
  const locale = useRouteLocale();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // Absent in a bare router (tests, a page outside the providers): an entity
  // then goes to the other country's home.
  const { client } = useContext(getApolloContext());
  // Which countries have sale pages (backend `locales`): unknown → Pakistan only.
  const { flags: saleFlags } = useSaleCountries();
  const latestSwitch = useRef(0);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Close on Escape, and hand focus back to the button that opened the list.
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  async function select(next: Locale) {
    setOpen(false);
    if (next.cc === locale.cc && next.lang === locale.lang) return;
    const plan = planCountrySwitch(pathname, search, next, saleFlags);
    const ticket = ++latestSwitch.current;
    if (plan.kind === 'stay') {
      // An account page has no prefix: only the remembered country changes.
      rememberLocale(next);
      return;
    }
    if (plan.kind === 'path') {
      navigate(plan.path);
      return;
    }
    const target = await resolveSwitchTarget(
      plan,
      next,
      async (type, key, to): Promise<EntityResolution | null> => {
        if (!client) return null;
        const result = await client.query<{ resolveEntityPath?: EntityResolution | null }>({
          query: RESOLVE_ENTITY_PATH,
          variables: resolveVariables(type, key, to),
          fetchPolicy: 'network-only',
        });
        return result.data?.resolveEntityPath ?? null;
      },
      // An event hub: the same event in the other country when it publishes it there.
      async (slug, to) => {
        if (!client) return false;
        const result = await client.query<{ salePage?: { event?: { slug?: string | null } | null } | null }>({
          query: SALE_PAGE,
          variables: { slug, locale: localeKey(to) },
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        });
        return result.data?.salePage?.event?.slug === slug;
      },
    );
    // A newer pick (or one that finished first) wins.
    if (target && ticket === latestSwitch.current) navigate(target);
  }

  const ICON_BTN =
    'relative flex h-10 items-center justify-center rounded-full px-2 text-ink-secondary transition-all duration-200 hover:bg-line-light hover:text-ink';

  return (
    // A disclosure: the button shows or hides a list of plain buttons (Tab
    // moves through them, Escape closes), not a listbox it would have to
    // drive with the arrow keys.
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={ICON_BTN}
        aria-label="Select country"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
      >
        <span className="text-lg leading-none" aria-hidden="true">
          {ccToFlag(locale.cc)}
        </span>
        <span className="ml-1.5 hidden text-sm font-medium sm:block">{locale.country}</span>
      </button>

      {open && (
        <ul
          id={listId}
          className="absolute right-0 top-full z-50 mt-1 min-w-[11rem] overflow-hidden rounded-xl border border-line bg-background shadow-lg"
          aria-label="Countries"
        >
          {LOCALES.map((loc) => {
            const isCurrent = loc.cc === locale.cc && loc.lang === locale.lang;
            return (
              <li key={`${loc.cc}-${loc.lang}`}>
                <button
                  aria-current={isCurrent ? 'true' : undefined}
                  type="button"
                  onClick={() => void select(loc)}
                  className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition-colors duration-150 ${
                    isCurrent
                      ? 'bg-primary/10 font-semibold text-primary-strong'
                      : 'text-ink hover:bg-line-light'
                  }`}
                >
                  <span className="text-base leading-none" aria-hidden="true">
                    {ccToFlag(loc.cc)}
                  </span>
                  <span>{loc.country}</span>
                  {isCurrent && (
                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary-strong" aria-hidden="true" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default CountrySelector;
